import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import BackButton from "@/components/BackButton";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function Payment() {
  const { regId } = useParams();
  const [searchParams] = useSearchParams();
  const activeRegId = regId || searchParams.get("registration_id");
  const qBook = searchParams.get("booking_id");
  const qMeal = searchParams.get("meal_booking_id");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reg, setReg] = useState(null);
  const [acc, setAcc] = useState(null);
  const [meal, setMeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (qMeal) {
          const mRes = await api.get("/meals/me");
          const m = mRes.data.find(x => x.booking_id === qMeal);
          setMeal(m || null);
        } else {
          const dRes = await api.get("/dashboard/summary");
          const r = dRes.data.registrations?.find(x => x.registration_id === activeRegId);
          setReg(r || null);
          if (qBook) {
            const a = dRes.data.accommodations?.find(x => x.booking_id === qBook);
            setAcc(a || null);
          }
        }
      } catch (e) {
        toast.error("Failed to load payment details");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeRegId, qBook, qMeal]);

  const confirm = useCallback(async () => {
    setPaying(true);
    try {
      let payload = {};
      if (qMeal) {
        payload = { meal_booking_id: qMeal };
      } else {
        payload = { registration_id: activeRegId };
        if (qBook) payload.booking_id = qBook;
      }
      const result = await payWithRazorpay(payload, user);
      if (!result) return; // user dismissed the payment modal
      setDone(result);
      toast.success("Payment confirmed!");
    } catch (e) {
      toast.error(e.response?.data?.detail ? formatError(e.response.data.detail) : e.message);
    } finally {
      setPaying(false);
    }
  }, [activeRegId, qBook, qMeal, user]);

  if (loading) return <div className="min-h-dvh bg-[#0B1429] flex items-center justify-center text-[#00F0FF]">LOADING...</div>;
  if ((!reg && !qMeal) || (qBook && !acc) || (qMeal && !meal)) return (
    <div className="min-h-dvh bg-[#0B1429] flex flex-col items-center justify-center text-white px-6 text-center gap-6">
      <div className="w-16 h-16 rounded-full bg-[#FBBF24]/10 flex items-center justify-center">
        <AlertTriangle className="w-8 h-8 text-[#FBBF24]" />
      </div>
      <div>
        <h2 className="font-display font-black text-2xl mb-2">Payment details not found</h2>
        <p className="text-[#94A3B8] text-sm max-w-xs">
          {!reg && !qMeal
            ? "We couldn't find a matching registration. It may have already been paid or the link is outdated."
            : qMeal && !meal
            ? "We couldn't find the meal booking linked to this payment. It may have already been paid."
            : "We couldn't find the accommodation booking linked to this payment. It may have already been paid."}
        </p>
      </div>
      <button
        onClick={() => navigate("/dashboard")}
        className="btn-primary"
      >
        Back to Dashboard
      </button>
    </div>
  );

  return (
    <div className="min-h-dvh bg-[#0B1429]">
      <Navbar />

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="bg-[#142040] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertTriangle className="w-5 h-5 text-[#FBBF24]" />
              Final Confirmation
            </DialogTitle>
            <DialogDescription className="text-[#94A3B8] pt-2 text-sm leading-relaxed">
              Once you proceed with the payment and it is confirmed, you will <strong>not be able to change your decision</strong> regarding your events and accommodation selection.
              <br /><br />
              Are you sure you want to proceed to checkout?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <button
              onClick={() => setShowConfirm(false)}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-white/20 hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowConfirm(false);
                confirm();
              }}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FBBF24] text-[#0B1429] hover:bg-white transition-colors ml-3"
            >
              Yes, proceed
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="pt-28 pb-20 max-w-4xl mx-auto px-4 sm:px-6">
        <BackButton className="mb-6" />
        {done ? (
          <div className="card-dark p-6 sm:p-10 text-center" data-testid="payment-success">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#00E676]/15 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-10 h-10 text-[#00E676]" />
            </div>
            <h2 className="font-display font-black text-3xl mb-2">Payment Confirmed</h2>
            <p className="text-[#94A3B8] mb-6">
              Reference: <span className="text-white font-mono">{done.razorpay_payment_id}</span>
            </p>
            <div className="bg-[#0B1429] border border-[#2A3A5F] rounded p-5 max-w-md mx-auto text-left text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-[#94A3B8]">Amount</span>
                <span className="font-semibold text-[#00F0FF]">{done.currency === "INR" ? "₹" : "$"}{done.amount_usd} {done.currency || "USD"}</span>
              </div>
              {activeRegId && (
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Registration</span>
                  <span>{activeRegId}</span>
                </div>
              )}
              {qBook && (
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Hotel</span>
                  <span>{qBook}</span>
                </div>
              )}
              {qMeal && (
                <div className="flex justify-between">
                  <span className="text-[#94A3B8]">Meal Booking</span>
                  <span>{qMeal}</span>
                </div>
              )}
            </div>
            <button onClick={() => navigate("/dashboard")} className="btn-primary mt-6 min-h-[44px] touch-manipulation">
              Go to Dashboard
            </button>
          </div>
        ) : (
          <>
            <div className="eyebrow mb-3">Confirm Registration</div>
            <h1 className="font-display font-black text-4xl mb-8">
              Complete your <span className="text-[#00F0FF]">registration</span>
            </h1>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-4">
                <p className="text-[#94A3B8] text-sm">
                  {qBook 
                    ? "Review your events and accommodation selection below. Click "
                    : "Review your event selection below and click "}
                  <strong className="text-white">Pay Now</strong> to securely complete payment via Razorpay and finalise your entry.
                </p>
              </div>

              <div className="card-dark p-6 h-fit" data-testid="payment-summary">
                <div className="eyebrow mb-3">Order Summary</div>
                
                {qMeal ? (
                  <>
                    <div className="text-sm space-y-1 mb-3">
                      <div className="text-white/80">Meal Booking ID</div>
                      <div className="font-mono text-[#00F0FF]">{meal.booking_id}</div>
                    </div>
                    <div className="text-sm mb-3">
                      <div className="flex justify-between text-[#94A3B8] mb-1">
                        <span>Meals ({meal.meals?.length || 0})</span>
                        <span>{meal.currency === "INR" ? "₹" : "$"}{meal.amount_usd}</span>
                      </div>
                      <ul className="text-xs space-y-0.5">
                        {meal.meals?.map((m, idx) => (
                          <li key={idx}>• {m.date} - {m.meal}</li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Events Section */}
                    <div className="text-sm space-y-1 mb-3">
                      <div className="text-white/80">Registration ID</div>
                      <div className="font-mono text-[#00F0FF]">{reg.registration_id}</div>
                    </div>
                    <div className="text-sm mb-3">
                      <div className="flex justify-between text-[#94A3B8] mb-1">
                        <span>Events ({reg.events?.length || 0})</span>
                        <span>{reg.currency === "INR" ? "₹" : "$"}{reg.amount_usd}</span>
                      </div>
                      <ul className="text-xs space-y-0.5">
                        {reg.events?.map((e) => (
                          <li key={e}>• {e}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Hotel Section */}
                    {acc && (
                      <>
                        <div className="divider-line my-3" />
                        <div className="text-sm space-y-1 mb-3">
                          <div className="text-white/80">Booking ID</div>
                          <div className="font-mono text-[#00F0FF]">{acc.booking_id}</div>
                        </div>
                        <div className="text-sm mb-3">
                          <div className="flex justify-between text-[#94A3B8] mb-1 gap-4">
                            <span>{acc.hotel_name}</span>
                            <span className="shrink-0">{acc.currency === "INR" ? "₹" : "$"}{acc.total_amount_usd}</span>
                          </div>
                          {acc.nights > 0 && (
                            <div className="text-xs text-[#94A3B8]">{acc.nights} nights ({acc.room_type})</div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                <div className="divider-line my-3" />
                <div className="flex justify-between font-display font-bold text-lg">
                  <span>Total</span>
                  <span className="text-[#00F0FF]">
                    {qMeal ? (meal.currency === "INR" ? "₹" : "$") : reg.currency === "INR" ? "₹" : "$"}
                    {qMeal ? meal.amount_usd : reg.amount_usd + (acc ? acc.total_amount_usd : 0)} {qMeal ? (meal.currency || "INR") : (reg.currency || "USD")}
                  </span>
                </div>
                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={paying}
                  data-testid="pay-button"
                  className="btn-primary w-full justify-center mt-5 min-h-[44px] touch-manipulation"
                >
                  {paying ? "Opening secure checkout..." : "Pay Now"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
